import type { CSSProperties, FC } from 'react';
import { useCallback, useMemo, memo } from 'react';
import classNames from 'classnames';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';

import { Button } from 'antd-mobile';
import type {
  ProductInfo,
  ProductInfoComboItem,
  ProductInfoComboProductType,
  ProductInfoCustomization,
} from '@/apis/product/types';
import { useChannelSupportSafeArea } from '@/hooks/useSupportSafeArea';
import { getCDNAssets } from '@/utils';

import type { SinglePopupProps } from '../../external/Popup';
import Popup from '../../external/Popup';
import { getExtraCustomizationCacheKey } from '../useEditor';
import { isDCSingleCombo } from '../func';
import { checkSingleComboProductIsDefault } from '../../utils';

import styles from './popup.module.less';

const getProductInfoText = (customization?: ProductInfoComboProductType['customization']) => {
  if (customization) {
    if (customization?.items && !isEmpty(customization.items)) {
      const _text = customization.items
        .map((i) => {
          if (i?.name && i.values) {
            const _v = i.values.find((v) => Number(v.checked) === 1);
            if (_v?.name) {
              return `${i.name}(${_v.name})`;
            }
          }
          return i?.name;
        })
        .filter(Boolean)
        .join('/');
      if (_text) {
        return _text;
      }
    }
    if (customization?.options && !isEmpty(customization.options)) {
      const _text = customization.options
        .filter((o) => Number(o.checked) === 1)
        .map((o) => o.name)
        .join('/');

      if (_text) {
        return _text;
      }

      if (customization.options.every((o) => o.checked === 0)) {
        return '都不要';
      }
    }
  }

  return '';
};

/**
 * 检测 DC 选择项是否为空
 * @param items
 */
export function checkDCProductInfoComboItemEmpty(items?: ProductInfoComboItem[]) {
  if (items && Array.isArray(items)) {
    for (let i = 0; i < items.length; i++) {
      const _combo = items[i];
      if (_combo) {
        const _isIncludeRound = Number(_combo.isIncludeRound) === 1;
        const _isSingle = isDCSingleCombo(_combo);
        if (_isIncludeRound || _isSingle) {
          const _r = _combo.comboProducts?.some((p) => checkSingleComboProductIsDefault(p));
          if (_r) {
            return false;
          }
        } else {
          const _r = _combo.comboProducts?.some((p) => p.quantity && p.quantity > 0);
          if (_r) {
            return false;
          }
        }
      }
    }
  }

  return true;
}

export type DCCustomListProductItem = ProductInfoComboProductType & {
  _showDel?: boolean;
  _pIndex?: number;
  _combo?: ProductInfoComboItem;
  _desc?: string;
};

export type CustomProductItemProps = {
  className?: string;
  style?: CSSProperties;
  data?: DCCustomListProductItem;
  onEdit?: (combo?: ProductInfoComboItem, product?: ProductInfoComboProductType, pIndex?: number) => void;
  onDel?: (combo?: ProductInfoComboItem, product?: ProductInfoComboProductType, pIndex?: number) => void;
};

const CustomProductItem: FC<CustomProductItemProps> = memo(function _CustomProductItem(props) {
  const { data, className, onEdit, onDel } = props;

  const handleEdit = useCallback(() => {
    onEdit?.(data?._combo, data, data?._pIndex);
  }, [data, onEdit]);
  const handleDel = useCallback(() => {
    onDel?.(data?._combo, data, data?._pIndex);
  }, [data, onDel]);

  if (!data) {
    return null;
  }
  return (
    <div className={classNames(styles.customProductItem, className)}>
      <img className={styles.customProductItemImg} src={data.image} />
      <div className={styles.customProductItemContent}>
        <div className={styles.customProductItemName}>{data.name}</div>
        {data._desc && (
          <div className={styles.customProductItemInfo} onClick={handleEdit}>
            <span className={styles.customProductItemInfoText}>{data._desc}</span>
            <img className={styles.customProductItemEditIcon} src="https://img.mcd.cn/gallery/85b72e0babdaf4f7.png" />
          </div>
        )}
      </div>
      {data._showDel && (
        <img
          className={styles.customProductItemDel}
          src={getCDNAssets('cart_icon_delete_oval_gray.png')}
          onClick={handleDel}
        />
      )}
    </div>
  );
});

type DCCustomListProps = {
  count?: number;
  data?: ProductInfo;
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onItemEdit?: CustomProductItemProps['onEdit'];
  onItemDel?: (
    combo?: ProductInfoComboItem,
    product?: ProductInfoComboProductType,
    pIndex?: number,
    total?: number,
  ) => void;
};

const DCCustomList: FC<DCCustomListProps> = (props) => {
  const { data, count = 1, itemsExtraCustomizationObj, onItemEdit, onItemDel } = props;

  const items = useMemo(
    () =>
      (flatMap(data?.comboItems, (c) => {
        const _isIncludeRound = Number(c.isIncludeRound) === 1;
        const _isSingle = isDCSingleCombo(c);
        const _combo = c;

        if (_isIncludeRound) {
          const _p = c.comboProducts?.find((p) => checkSingleComboProductIsDefault(p));

          const _pcode = _p?.code;
          if (_pcode) {
            if (_p.quantity && _p.quantity > 1) {
              return Array.from({ length: _p.quantity }, (_, i) => i).map((idx) => {
                const _customization =
                  itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, _pcode, idx)] || _p.customization;
                return { ..._p, _combo, _desc: getProductInfoText(_customization), _pIndex: idx, quantity: 1 };
              });
            }
            const _customization =
              itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, _pcode)] || _p.customization;
            return { ..._p, _combo, _desc: getProductInfoText(_customization) };
          }
        } else if (_isSingle) {
          const _p = c.comboProducts?.find((p) => checkSingleComboProductIsDefault(p));

          if (_p && _p.code) {
            const _customization =
              itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, _p.code)] || _p.customization;
            return { ..._p, _combo, _desc: getProductInfoText(_customization) };
          }
        } else {
          const _plist = c.comboProducts?.filter((p) => p.quantity && p.quantity > 0);
          return flatMap(_plist, (p) => {
            const _quantity = p.quantity;
            if (_quantity && _quantity > 1) {
              return Array.from({ length: _quantity }, (_, i) => i).map((idx) => {
                const _customization =
                  (p.code && itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, p.code, idx)]) ||
                  p.customization;
                const _desc = getProductInfoText(_customization);
                return {
                  ...p,
                  quantity: 1,
                  _combo,
                  _showDel: true,
                  _pIndex: idx,
                  _desc,
                };
              });
            }

            const _customization =
              (p.code && itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, p.code, 0)]) || p.customization;
            const _desc = getProductInfoText(_customization);
            return { ...p, _combo, _showDel: true, _pIndex: 0, _desc };
          });
        }
        return null;
      }).filter(Boolean) as DCCustomListProductItem[]) || [],
    [data?.comboItems, itemsExtraCustomizationObj],
  );

  const itemsLen = items.length;

  const handelItemDel = useCallback<Required<CustomProductItemProps>['onDel']>(
    (c, p, pIdx) => {
      onItemDel?.(c, p, pIdx, itemsLen);
    },
    [itemsLen, onItemDel],
  );

  const listNode = useMemo(
    () =>
      items?.map((p, pIdx) => (
        <CustomProductItem
          key={`customComboProduct${p.code || ''}${pIdx}`}
          data={p}
          onEdit={onItemEdit}
          onDel={handelItemDel}
        />
      )),
    [items, handelItemDel, onItemEdit],
  );

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          {data?.name}
          <span className={styles.headerTitleSymbol}> × </span>
          {count}
        </div>
        <div className={styles.headerSub}>
          共包含&nbsp;<span className={styles.headerSubNum}>{itemsLen}</span>&nbsp;份
        </div>
      </div>
      <div className={styles.content}>{listNode}</div>
    </>
  );
};

export type DCCustomListPopupProps = Pick<SinglePopupProps, 'visible' | 'destroyOnClose' | 'onMaskClick'> & {
  count?: number;
  data?: ProductInfo;
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onOk?: () => void;
  onItemEdit?: DCCustomListProps['onItemEdit'];
  onItemDel?: DCCustomListProps['onItemDel'];
};

const DCCustomListPopup: FC<DCCustomListPopupProps> = (props) => {
  const { count = 1, data, itemsExtraCustomizationObj, onOk, onItemEdit, onItemDel, ...rest } = props;

  const supportSafeArea = useChannelSupportSafeArea();

  return (
    <Popup destroyOnClose bodyClassName={styles.popupBody} {...rest}>
      <DCCustomList
        count={count}
        data={data}
        itemsExtraCustomizationObj={itemsExtraCustomizationObj}
        onItemEdit={onItemEdit}
        onItemDel={onItemDel}
      />
      <div className={styles.footer} data-supportsafearea={supportSafeArea}>
        <Button shape="rounded" color="primary" block data-blod onClick={onOk}>
          好的
        </Button>
      </div>
    </Popup>
  );
};

export default DCCustomListPopup;
